// ===============================================================
// file: Leads.js
// ===============================================================
// This version fixes the Firestore paths so that “leads” are read
// and written from the root-level “contacts” collection (same as
// the Contacts component).  All CRUD operations now point to
// db.collection("contacts") instead of nested personal / team
// paths.  Team switching UI is kept, but currently acts only as
// a visual filter – all data lives in the shared collection.
// ===============================================================

import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
  addDoc,
  setDoc,
} from "firebase/firestore";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileImport,
  FaFileExport,
  FaEye,
  FaRobot,
  FaCopy,
  FaPhone,
  FaSpinner,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import AddLeadModal from "./components/AddLeadModal";
import ImportLeadsModal from "./components/ImportLeadsModal";
import ContactEditHistory from "./components/ContactEditHistory";
import EmailGeneratorModal from "./components/EmailGeneratorModal";
import VoiceMailService from "./services/VoiceMailService";

// Helper – format large numbers
const formatNumber = (n) => {
  if (n == null) return "";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

// Email sub-tabs keys
const EMAIL_TABS = [
  { key: "day1-problem-solution", label: "D1" },
  { key: "day4-case-study", label: "D4" },
  { key: "day7-role-value", label: "D7" },
  { key: "day10-urgency", label: "D10" },
  { key: "day14-breakup", label: "D14" },
];

// Root collection to use for all leads / contacts
const ROOT_COLL = "contacts";

// =============================================================
// MAIN COMPONENT
// =============================================================
const Leads = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEmailGenerator, setShowEmailGenerator] = useState(false);

  // Filters
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState("contact");
  const [emailTab, setEmailTab] = useState(EMAIL_TABS[0].key);
  const [emailTakeTab, setEmailTakeTab] = useState("standard");
  const [voicemailScript, setVoicemailScript] = useState(null);
  const [generatingVoicemail, setGeneratingVoicemail] = useState(false);

  // Team UI state (still present but not used for Firestore paths)
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [personalView, setPersonalView] = useState(true);

  // Fetch leads on mount or when personal/team toggles change
  useEffect(() => {
    if (user && teamId) fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, teamId]);

  // Build filters when inputs change
  useEffect(() => {
    let result = [...leads];
    const term = searchTerm.toLowerCase();
    if (term) {
      result = result.filter(
        (l) =>
          l.fullName?.toLowerCase().includes(term) ||
          l.email?.toLowerCase().includes(term) ||
          l.companyName?.toLowerCase().includes(term) ||
          l.title?.toLowerCase().includes(term) ||
          l.industry?.toLowerCase().includes(term)
      );
    }
    if (filterCompany)
      result = result.filter((l) => l.companyName === filterCompany);
    if (filterRole) result = result.filter((l) => l.title === filterRole);
    setFilteredLeads(result);
  }, [leads, searchTerm, filterCompany, filterRole]);

  // Unique company / role lists
  const companies = Array.from(
    new Set(leads.map((l) => l.companyName).filter(Boolean))
  ).sort();
  const roles = Array.from(
    new Set(leads.map((l) => l.title).filter(Boolean))
  ).sort();

  // Pagination helpers
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };
  const changeItemsPerPage = (val) => {
    setItemsPerPage(Number(val));
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilterCompany("");
    setFilterRole("");
    setCurrentPage(1);
  };

  // Open view modal
  const openLeadView = (lead) => {
    setSelectedLead(lead);
    setActiveTab("contact");
    setEmailTab(EMAIL_TABS[0].key);
    setEmailTakeTab("standard");
    setVoicemailScript(null);
    setShowViewModal(true);
  };

  // Copy email to clipboard
  const copyEmailToClipboard = (emailContent) => {
    navigator.clipboard.writeText(emailContent);
    toast.success("Email copied to clipboard!");
  };

  // Generate voicemail from selected email
  const generateVoicemail = async () => {
    if (!selectedLead) return;

    // Use the currently selected email as the email sample
    const emailContent =
      emailTakeTab === "standard"
        ? selectedLead[emailTab]
        : selectedLead.generatedEmailSamples &&
          selectedLead.generatedEmailSamples.find(
            (s) => s.sampleId === emailTakeTab
          ) &&
          selectedLead.generatedEmailSamples.find(
            (s) => s.sampleId === emailTakeTab
          ).emails[emailTab];

    if (!emailContent) {
      toast.error("No email content selected. Please select an email first.");
      return;
    }

    setGeneratingVoicemail(true);
    try {
      const result = await VoiceMailService.generateVoiceMail({
        emailSample: emailContent,
        contactName: selectedLead.fullName || selectedLead.name || "",
        contactTitle: selectedLead.title || "",
        companyName: selectedLead.companyName || "",
        contactEmail: selectedLead.email || "",
        sellerName: user.displayName || user.email.split("@")[0] || "",
        sellerRole: "Sales Development Representative",
        sellerCompany: "ReWorld",
        campaignGoal: "Book a meeting",
      });

      setVoicemailScript(result);
      toast.success("Voicemail script generated!");
    } catch (error) {
      console.error("Error generating voicemail:", error);
      toast.error(error.message || "Failed to generate voicemail");
    } finally {
      setGeneratingVoicemail(false);
    }
  };

  // Copy voicemail to clipboard
  const copyVoicemailToClipboard = () => {
    if (!voicemailScript || !voicemailScript.script) return;
    navigator.clipboard.writeText(voicemailScript.script);
    toast.success("Voicemail script copied to clipboard!");
  };

  // Download single take emails
  const downloadTakeEmails = (take) => {
    const isStandard = take === "standard";
    let content = `Email Sequence - ${
      isStandard ? "Standard" : take.sampleName
    }\n`;
    content += `Contact: ${selectedLead.fullName || selectedLead.name}\n`;
    content += `Email: ${selectedLead.email}\n`;
    content += `Company: ${selectedLead.companyName || "N/A"}\n`;
    if (!isStandard) {
      content += `Generated: ${new Date(take.generatedAt).toLocaleString()}\n`;
      if (take.generatedBy) content += `By: ${take.generatedBy}\n`;
    }
    content += `\n${"=".repeat(60)}\n\n`;

    EMAIL_TABS.forEach(({ key, label }) => {
      const email = isStandard ? selectedLead[key] : take.emails[key];
      if (email) {
        content += `${label.replace("D", "Day ")}\n`;
        content += `${"-".repeat(30)}\n`;
        // Remove markdown formatting
        const cleanEmail = email
          .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold
          .replace(/\*([^*]+)\*/g, "$1") // Remove italic
          .replace(/^#+ /gm, "") // Remove headers
          .replace(/^- /gm, "• ") // Convert dashes to bullets
          .trim();
        content += `${cleanEmail}\n\n`;
      }
    });

    // Create and download file
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName = `${selectedLead.fullName || selectedLead.name}_${
      isStandard ? "standard" : take.sampleId
    }_emails_${new Date().toISOString().split("T")[0]}.txt`;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(
      `${isStandard ? "Standard" : take.sampleName} emails downloaded!`
    );
  };

  // Fetch all leads from team collection
  const fetchLeads = async () => {
    if (!user || !teamId) return;
    setLoading(true);
    try {
      // Try team collection first, fallback to root collection
      let q = query(
        collection(db, "teams", teamId, "contacts"),
        orderBy("createdAt", "desc")
      );
      let snap = await getDocs(q);

      if (snap.empty) {
        console.log("No team contacts found, falling back to root collection");
        q = query(collection(db, ROOT_COLL), orderBy("createdAt", "desc"));
        snap = await getDocs(q);
        toast.info("Loading from root collection - migration recommended");
      }

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
      }));
      setLeads(list);
      setFilteredLeads(list);
      toast.success(`Loaded ${list.length} leads`);
    } catch (e) {
      console.error("Error fetching leads:", e);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  // Delete a lead
  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteDoc(doc(db, ROOT_COLL, id));
      setLeads((p) => p.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      console.error("Delete error:", e);
      toast.error("Failed to delete lead");
    }
  };

  // Save (add / update) a lead
  const handleSaveLead = async (lead) => {
    if (!user) return;
    try {
      setLoading(true);
      const now = new Date();
      const data = { ...lead, updatedAt: now };
      if (lead.id) {
        await setDoc(doc(db, ROOT_COLL, lead.id), data, { merge: true });
        setLeads((p) =>
          p.map((l) => (l.id === lead.id ? { ...data, id: lead.id } : l))
        );
        toast.success("Lead updated");
      } else {
        data.createdAt = now;
        const ref = await addDoc(collection(db, ROOT_COLL), data);
        setLeads((p) => [{ ...data, id: ref.id }, ...p]);
        toast.success("Lead added");
      }
      setShowAddLeadModal(false);
      setSelectedLead(null);
    } catch (e) {
      console.error("Save error:", e);
      toast.error("Failed to save lead");
    } finally {
      setLoading(false);
    }
  };

  // Import leads CSV save
  const handleImportSave = async (imported) => {
    if (!user) return;
    try {
      setLoading(true);
      const now = new Date();
      const batchAdds = imported.map((l) =>
        addDoc(collection(db, ROOT_COLL), { ...l, createdAt: now })
      );
      await Promise.all(batchAdds);
      toast.success(`Imported ${imported.length} leads`);
      setShowImportModal(false);
      fetchLeads();
    } catch (e) {
      console.error("Import error:", e);
      toast.error("Failed to import leads");
    } finally {
      setLoading(false);
    }
  };

  // Export leads as CSV
  const handleExportLeads = () => {
    const headers = [
      "fullName",
      "email",
      "companyName",
      "title",
      "industry",
      "createdAt",
    ];
    const csv = [
      headers.join(","),
      ...filteredLeads.map((l) =>
        headers
          .map((h) => {
            const v = l[h];
            if (h === "createdAt") return v ? new Date(v).toISOString() : "";
            return v ? `"${v.toString().replace(/"/g, '""')}"` : "";
          })
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Leads Management</h1>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
          >
            <FaPlus className="mr-2" /> Add Lead
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full border rounded-md p-2 pl-10 focus:ring-2 focus:ring-purple-500"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="border rounded-md p-2 flex-1"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <select
              className="border rounded-md p-2 flex-1"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            <button
              onClick={clearFilters}
              className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
            >
              Clear
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
            >
              <FaFileImport className="mr-2" /> Import
            </button>
            <button
              onClick={handleExportLeads}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
            >
              <FaFileExport className="mr-2" /> Export
            </button>
          </div>
        </div>

        {/* Pagination controls */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <span>Show</span>
            <select
              className="border rounded px-2 py-1"
              value={itemsPerPage}
              onChange={(e) => changeItemsPerPage(e.target.value)}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
            <span>per page</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="border px-3 py-1 rounded"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              className="border px-3 py-1 rounded"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <colgroup>
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead className="bg-gray-50 text-left">
                <tr className="uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Industry</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-3 py-6 text-center">
                      <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent mx-auto rounded-full"></div>
                    </td>
                  </tr>
                ) : paginatedLeads.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No leads
                    </td>
                  </tr>
                ) : (
                  paginatedLeads.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-900 leading-tight">
                        <div className="flex flex-col">
                          <span
                            className="block truncate font-medium"
                            title={l.fullName || "—"}
                          >
                            {l.fullName || "—"}
                          </span>
                          <span
                            className="text-xs text-gray-500 truncate"
                            title={l.email}
                          >
                            {l.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 leading-tight">
                        <span
                          className="block truncate"
                          title={l.companyName || "—"}
                        >
                          {l.companyName || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 leading-tight">
                        <span className="block truncate" title={l.title || "—"}>
                          {l.title || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-600 leading-tight">
                        <span
                          className="block truncate text-xs"
                          title={l.industry || "—"}
                        >
                          {l.industry || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-600 leading-tight">
                        <span
                          className="block truncate text-xs"
                          title={l.location || "—"}
                        >
                          {l.location || "—"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openLeadView(l)}
                            className="h-8 w-8 flex items-center justify-center rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            aria-label="View lead"
                            title="View lead"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedLead(l) || setShowAddLeadModal(true)
                            }
                            className="h-8 w-8 flex items-center justify-center rounded-md text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            aria-label="Edit lead"
                            title="Edit lead"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteLead(l.id)}
                            className="h-8 w-8 flex items-center justify-center rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                            aria-label="Delete lead"
                            title="Delete lead"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showAddLeadModal && (
        <AddLeadModal
          lead={selectedLead}
          onClose={() => {
            setShowAddLeadModal(false);
            setSelectedLead(null);
          }}
          onSave={handleSaveLead}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportLeadsModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImportSave}
        />
      )}

      {/* Email Generator Modal */}
      {showEmailGenerator && selectedLead && (
        <EmailGeneratorModal
          contact={selectedLead}
          teamId={teamId}
          onClose={() => {
            setShowEmailGenerator(false);
            setShowViewModal(true); // Reopen the contact detail modal
          }}
          onSave={(emailSamples) => {
            // Refresh the lead data to show new samples
            fetchLeads();
            setShowEmailGenerator(false);
            setShowViewModal(true); // Reopen the contact detail modal
            setActiveTab("emails"); // Go directly to emails tab to see new takes
          }}
        />
      )}

      {/* View Modal */}
      {showViewModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex border-b mb-4">
              {["contact", "emails", "voicemail"].map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`px-4 py-2 ${
                    activeTab === t
                      ? "border-b-2 border-purple-600 font-semibold"
                      : "text-gray-600"
                  }`}
                >
                  {t === "contact"
                    ? "Contact Info"
                    : t === "emails"
                    ? "Emails"
                    : "Voice Mail"}
                </button>
              ))}
            </div>
            {activeTab === "contact" && (
              <div className="space-y-4">
                {/* Basic Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Name", selectedLead.fullName],
                    ["Email", selectedLead.email],
                    ["Role", selectedLead.title],
                    ["Industry", selectedLead.industry || "—"],
                    ["Company", selectedLead.companyName || "—"],
                    ["Location", selectedLead.location || "—"],
                    ["Phone", selectedLead.phone || "—"],
                    ["Hunter Status", selectedLead.hunterStatus || "—"],
                  ].map(([lab, val]) => (
                    <div key={lab} className="flex flex-col">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {lab}
                      </span>
                      <span className="text-sm text-gray-900">
                        {val || "—"}
                      </span>
                    </div>
                  ))}

                  {/* LinkedIn URL as clickable link */}
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      LinkedIn
                    </span>
                    {selectedLead.linkedinUrl || selectedLead.url ? (
                      <a
                        href={selectedLead.linkedinUrl || selectedLead.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        View Profile →
                      </a>
                    ) : (
                      <span className="text-sm text-gray-900">—</span>
                    )}
                  </div>

                  {/* Domain */}
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Domain
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedLead.domain || "—"}
                    </span>
                  </div>
                </div>

                {/* Rich Data Sections */}
                {(selectedLead.personSummary || selectedLead.summary) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      LinkedIn Profile Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-40 overflow-y-auto">
                        {selectedLead.personSummary || selectedLead.summary}
                      </pre>
                    </div>
                  </div>
                )}

                {(selectedLead.companySummary ||
                  selectedLead.company_summary) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Company Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-40 overflow-y-auto">
                        {selectedLead.companySummary ||
                          selectedLead.company_summary}
                      </pre>
                    </div>
                  </div>
                )}

                {(selectedLead.workExperience || selectedLead.work_ex) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                      Work Experience
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-40 overflow-y-auto">
                        {selectedLead.workExperience || selectedLead.work_ex}
                      </pre>
                    </div>
                  </div>
                )}

                {selectedLead.education && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      Education
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-40 overflow-y-auto">
                        {selectedLead.education}
                      </pre>
                    </div>
                  </div>
                )}

                {(selectedLead.linkedinSummary ||
                  selectedLead.linkedin_summary) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                      LinkedIn Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-40 overflow-y-auto">
                        {selectedLead.linkedinSummary ||
                          selectedLead.linkedin_summary}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Contact Edit History and Metadata */}
                <ContactEditHistory
                  contact={selectedLead}
                  onEdit={(contact) => {
                    setSelectedLead(contact);
                    setShowViewModal(false);
                    setShowAddLeadModal(true);
                  }}
                />
              </div>
            )}
            {activeTab === "emails" && (
              <>
                {/* Email Takes Tabs */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-2 border-b">
                    {/* Standard emails tab */}
                    <button
                      onClick={() => setEmailTakeTab("standard")}
                      className={`px-3 py-2 rounded-t font-medium ${
                        emailTakeTab === "standard"
                          ? "bg-blue-600 text-white border-b-2 border-blue-600"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Standard
                    </button>

                    {/* Generated takes tabs */}
                    {selectedLead.generatedEmailSamples &&
                      selectedLead.generatedEmailSamples.map(
                        (sample, index) => (
                          <button
                            key={sample.sampleId}
                            onClick={() => setEmailTakeTab(sample.sampleId)}
                            className={`px-3 py-2 rounded-t font-medium ${
                              emailTakeTab === sample.sampleId
                                ? "bg-purple-600 text-white border-b-2 border-purple-600"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Take {index + 1}
                            <span className="ml-1 text-xs opacity-75">
                              (
                              {new Date(
                                sample.generatedAt
                              ).toLocaleDateString()}
                              )
                            </span>
                          </button>
                        )
                      )}
                  </div>

                  <button
                    onClick={() => {
                      setShowEmailGenerator(true);
                      setShowViewModal(false); // Close the contact detail modal
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center text-sm"
                  >
                    <FaRobot className="mr-2" />
                    Generate New Take
                  </button>
                </div>

                {/* Email Day Tabs */}
                <div className="flex space-x-2 border-b mb-4">
                  {EMAIL_TABS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setEmailTab(t.key)}
                      className={`px-3 py-1 rounded-t text-sm ${
                        emailTab === t.key
                          ? "bg-gray-800 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Email Content Display */}
                <div className="bg-gray-50 p-4 rounded overflow-y-auto max-h-[60vh]">
                  {emailTakeTab === "standard" ? (
                    <div>
                      <div className="flex justify-between items-center mb-3 pb-2 border-b">
                        <span className="font-medium text-sm">
                          Original Email
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              copyEmailToClipboard(selectedLead[emailTab])
                            }
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center"
                          >
                            <FaCopy className="mr-1" />
                            Copy
                          </button>
                          <button
                            onClick={() => downloadTakeEmails("standard")}
                            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 flex items-center"
                          >
                            <FaFileExport className="mr-1" />
                            Download Standard
                          </button>
                        </div>
                      </div>
                      <pre className="whitespace-pre-wrap">
                        {selectedLead[emailTab] || "No content"}
                      </pre>
                    </div>
                  ) : (
                    (() => {
                      const selectedTake =
                        selectedLead.generatedEmailSamples?.find(
                          (sample) => sample.sampleId === emailTakeTab
                        );
                      return (
                        <div>
                          {selectedTake ? (
                            <>
                              <div className="flex justify-between items-center mb-3 pb-2 border-b">
                                <div>
                                  <span className="font-medium text-sm">
                                    {selectedTake.sampleName}
                                  </span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    Generated{" "}
                                    {new Date(
                                      selectedTake.generatedAt
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      copyEmailToClipboard(
                                        selectedTake.emails[emailTab]
                                      )
                                    }
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center"
                                  >
                                    <FaCopy className="mr-1" />
                                    Copy
                                  </button>
                                  <button
                                    onClick={() =>
                                      downloadTakeEmails(selectedTake)
                                    }
                                    className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 flex items-center"
                                  >
                                    <FaFileExport className="mr-1" />
                                    Download Take
                                  </button>
                                </div>
                              </div>
                              <pre className="whitespace-pre-wrap">
                                {selectedTake.emails[emailTab] ||
                                  "No content for this day"}
                              </pre>
                            </>
                          ) : (
                            <div className="text-center text-gray-500 py-8">
                              <FaRobot className="mx-auto mb-2 text-2xl" />
                              <p>Selected take not found</p>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>
              </>
            )}
            {activeTab === "voicemail" && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <FaPhone className="mr-2" />
                    Voice Mail Script Generator
                  </h3>
                  <p className="text-xs text-blue-700 mb-3">
                    Generate a 30-second voicemail script based on the selected
                    email below.
                  </p>

                  {/* Currently Selected Email Indicator */}
                  <div className="bg-white border border-blue-300 rounded p-3 mb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Using Email:
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {emailTakeTab === "standard" ? (
                              <>
                                Standard -{" "}
                                {EMAIL_TABS.find((t) => t.key === emailTab)
                                  .label || emailTab}
                              </>
                            ) : (
                              <>
                                {selectedLead.generatedEmailSamples &&
                                  selectedLead.generatedEmailSamples.find(
                                    (s) => s.sampleId === emailTakeTab
                                  ) &&
                                  selectedLead.generatedEmailSamples.find(
                                    (s) => s.sampleId === emailTakeTab
                                  ).sampleName}{" "}
                                -{" "}
                                {EMAIL_TABS.find((t) => t.key === emailTab)
                                  .label || emailTab}
                              </>
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Preview:{" "}
                          {((emailTakeTab === "standard"
                            ? selectedLead[emailTab]
                            : selectedLead.generatedEmailSamples &&
                              selectedLead.generatedEmailSamples.find(
                                (s) => s.sampleId === emailTakeTab
                              ) &&
                              selectedLead.generatedEmailSamples.find(
                                (s) => s.sampleId === emailTakeTab
                              ).emails[emailTab]) &&
                            (emailTakeTab === "standard"
                              ? selectedLead[emailTab]
                              : selectedLead.generatedEmailSamples.find(
                                  (s) => s.sampleId === emailTakeTab
                                ).emails[emailTab]
                            ).slice(0, 100)) ||
                            "No email selected"}
                          ...
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setActiveTab("emails");
                        }}
                        className="text-xs bg-white border border-blue-300 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 flex items-center ml-3"
                      >
                        Change Email
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={generateVoicemail}
                    disabled={generatingVoicemail}
                    className={`w-full inline-flex justify-center items-center px-4 py-2 rounded-md text-sm font-medium ${
                      generatingVoicemail
                        ? "bg-purple-200 text-purple-500 cursor-not-allowed"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                  >
                    {generatingVoicemail ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FaPhone className="mr-2" />
                        Generate Voicemail from This Email
                      </>
                    )}
                  </button>
                </div>

                {voicemailScript && (
                  <div className="bg-white border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b">
                      <h3 className="text-md font-semibold text-purple-800 flex items-center">
                        <FaPhone className="mr-2" />
                        Ready to Leave
                      </h3>
                      <button
                        onClick={copyVoicemailToClipboard}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        <FaCopy className="mr-2" />
                        Copy Script
                      </button>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-md">
                      <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                        {voicemailScript.script}
                      </p>
                    </div>

                    {voicemailScript.callToAction && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1">
                          CTA to Emphasize:
                        </p>
                        <p className="text-sm text-blue-800">
                          {voicemailScript.callToAction}
                        </p>
                      </div>
                    )}

                    {voicemailScript.keyPoints &&
                      voicemailScript.keyPoints.length > 0 && (
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            Key Reminders:
                          </p>
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {voicemailScript.keyPoints.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                    <div className="text-xs text-gray-500 pt-2 border-t">
                      <p className="flex items-center">
                        <FaInfoCircle className="mr-2" />
                        Tip: Practice reading aloud before calling. Aim for 30
                        seconds or less.
                      </p>
                    </div>
                  </div>
                )}

                {!voicemailScript && !generatingVoicemail && (
                  <div className="text-center py-12 text-gray-500">
                    <FaPhone className="mx-auto text-4xl mb-3 text-gray-300" />
                    <p className="text-sm">
                      Select an email from the Emails tab, then generate your
                      voicemail script.
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
